import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, ShieldAlert, ListFilter } from "lucide-react";
import { ProviderHealthCard, type ProviderHealthData } from "@/components/models/ProviderHealthCard";
import { BlockListEditor } from "@/components/models/BlockListEditor";
import { DecisionsFeed } from "@/components/models/DecisionsFeed";
import { useOrgRole } from "@/hooks/useOrgRole";

type TabKey = "health" | "blocklist" | "decisions";
const VALID: TabKey[] = ["health", "blocklist", "decisions"];

export default function ModelsHealth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { orgId } = useOrgRole();
  const raw = (params.get("tab") || "health") as TabKey;
  const tab: TabKey = VALID.includes(raw) ? raw : "health";
  const setTab = (v: string) => navigate(`/admin/models/health?tab=${v}`, { replace: true });

  // Health: agrega provider_health + model_decisions (p50/p95/cost)
  const { data: healthData = [] } = useQuery({
    queryKey: ["provider-health-agg", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<ProviderHealthData[]> => {
      // 1. provider_health snapshot
      const { data: ph } = await supabase
        .from("provider_health")
        .select("provider_name, status, last_error_code, last_error_at, cooldown_until")
        .eq("org_id", orgId!);

      // 2. recent decisions for p50/p95/cost
      const since7d = new Date(Date.now() - 7 * 86400_000).toISOString();
      const { data: decisions } = await supabase
        .from("model_decisions")
        .select("provider_used, latency_ms, estimated_cost_usd, created_at")
        .eq("org_id", orgId!)
        .gte("created_at", since7d)
        .limit(5000);

      const since24h = new Date(Date.now() - 86400_000).toISOString();
      const { data: decisions24 } = await supabase
        .from("model_decisions")
        .select("provider_used, was_fallback, created_at")
        .eq("org_id", orgId!)
        .gte("created_at", since24h)
        .limit(5000);

      const providerSet = new Set<string>();
      (ph ?? []).forEach((p) => providerSet.add(p.provider_name.toLowerCase()));
      (decisions ?? []).forEach((d) => d.provider_used && providerSet.add(d.provider_used.toLowerCase()));
      // Always show the 3 main upstreams even if no traffic
      ["anthropic", "openai", "google"].forEach((p) => providerSet.add(p));

      const out: ProviderHealthData[] = [];
      for (const provider of providerSet) {
        const phRow = (ph ?? []).find((p) => p.provider_name.toLowerCase() === provider);
        const decsForProv = (decisions ?? []).filter((d) =>
          (d.provider_used ?? "").toLowerCase().includes(provider)
        );
        const decs24 = (decisions24 ?? []).filter((d) =>
          (d.provider_used ?? "").toLowerCase().includes(provider)
        );
        const latencies = decsForProv.map((d) => d.latency_ms).filter((l): l is number => typeof l === "number").sort((a, b) => a - b);
        const p50 = latencies.length > 0 ? latencies[Math.floor(latencies.length * 0.5)] : undefined;
        const p95 = latencies.length > 0 ? latencies[Math.floor(latencies.length * 0.95)] : undefined;
        const cost = decsForProv.reduce((sum, d) => sum + (Number(d.estimated_cost_usd) || 0), 0);
        const successCount = decs24.filter((d) => !d.was_fallback).length;
        const successRate = decs24.length > 0 ? successCount / decs24.length : undefined;

        out.push({
          provider_name: provider,
          status: (phRow?.status as ProviderHealthData["status"]) ?? (decs24.length > 0 ? "ok" : "unknown"),
          p50_ms: p50,
          p95_ms: p95,
          cost_7d_usd: cost > 0 ? cost : undefined,
          last_error_at: phRow?.last_error_at ?? null,
          last_error_code: phRow?.last_error_code ?? null,
          cooldown_until: phRow?.cooldown_until ?? null,
          call_count_24h: decs24.length,
          success_rate_24h: successRate,
        });
      }
      return out.sort((a, b) => a.provider_name.localeCompare(b.provider_name));
    },
    refetchInterval: 60_000,
  });

  return (
    <div className="container mx-auto px-4 py-4 max-w-7xl">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Saúde dos modelos</h1>
        <p className="text-sm text-muted-foreground">
          Painel consolidado de routing, block-list e decisões em tempo real.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="health" className="gap-2">
            <Activity className="w-4 h-4" /> Saúde
          </TabsTrigger>
          <TabsTrigger value="blocklist" className="gap-2">
            <ShieldAlert className="w-4 h-4" /> Block-list
          </TabsTrigger>
          <TabsTrigger value="decisions" className="gap-2">
            <ListFilter className="w-4 h-4" /> Decisões
          </TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {healthData.map((h) => (
              <ProviderHealthCard key={h.provider_name} data={h} />
            ))}
            {healthData.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full">Nenhum dado de saúde disponível.</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="blocklist" className="mt-4">
          <BlockListEditor />
        </TabsContent>

        <TabsContent value="decisions" className="mt-4">
          <DecisionsFeed />
        </TabsContent>
      </Tabs>
    </div>
  );
}
